import { APIClient } from '../../helpers/api_helper';
import secureApi from '../../utils/secureApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const api = new APIClient();

// =========================
// Player Interfaces for Manage Players
// =========================

export interface ManagePlayer {
  _id: string;
  playerId?: string;
  name: string;
  profilePic: string;
  dob: string;
  ageCategory: string;
  parentName?: string; // Added parent name field
  verificationStatus: 'pending' | 'approved' | 'referred_back';
  createdAt: string;
  amountPaid?: number;
  amountDue?: number;
  qrCode?: string;
  playerIdCard?: string;
  remark?: string; // Computed field - last remark from remarks array
  remarks?: Array<{
    verificationStatus: string;
    remark: string | null;
    date: string;
    by: string;
    _id: string;
  }>;
  teamInfo?: {
    _id: string;
    club: string;
    name: string;
    logo: string;
    clubInfo?: {
      _id: string;
      name: string;
      logo: string;
    };
  };
  documents?: PlayerDocument[];
}

export interface PlayerDocument {
  fileName: string;
  key: string;
  type: 'id-proof' | 'consent-form';
}

export interface ListAllPlayersResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    paginatedResults: ManagePlayer[];
    totalCount: number;
  };
}

export interface UpdateVerificationStatusRequest {
  verificationStatus: 'approved' | 'referred_back';
  remark?: string | null;
}

export interface UpdateVerificationStatusResponse {
  success: boolean;
  message: string;
  data?: any;
}

export interface UpdatePlayerDetailsRequest {
  name: string;
  dob: string;
  ageCategory: string;
  parentName?: string; // Added parent name field
  profilePic?: string;
  documents?: PlayerDocument[];
}

export interface UpdatePlayerDetailsResponse {
  success: boolean;
  message: string;
  data?: ManagePlayer;
}

// =========================
// Manage Players APIs
// =========================

/**
 * Fetch all players with pagination
 * @param pageNum - Page number for pagination (default: 1)
 * @param pageSize - Number of items per page (default: 200)
 * @returns Promise<ListAllPlayersResponse>
 */
export const getAllPlayers = async (pageNum: number = 1, pageSize: number = 200): Promise<ListAllPlayersResponse> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    // APIClient handles token management automatically
    const response = await api.get(`${API_BASE_URL}/api/v1/club/list-all-players?pageNum=${pageNum}&pageSize=${pageSize}`, null);
    
    // Handle the API response structure
    if (response && response.data) {
      return response.data;
    }
    
    throw new Error('Invalid response structure from all players API');
  } catch (error) {
    console.error('Failed to fetch all players:', error);
    throw error;
  }
};

/**
 * Update player verification status
 * @param playerId - The player ID
 * @param statusData - Verification status data
 * @returns Promise<UpdateVerificationStatusResponse>
 */
export const updatePlayerVerificationStatus = async (
  playerId: string, 
  statusData: UpdateVerificationStatusRequest
): Promise<UpdateVerificationStatusResponse> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    const response = await secureApi.post<any>(
      `/api/v1/club/team/player/update-verification-status/${playerId}`,
      statusData
    );

    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    const responseData = (response as any)?.data || response;

    return {
      success: true,
      message: 'Verification status updated successfully',
      data: responseData
    };
  } catch (error: any) {
    console.error('Failed to update verification status:', error);
    throw new Error(error.response?.data?.message || 'Failed to update verification status');
  }
};

/**
 * Update player details
 * @param playerId - The player ID
 * @param playerData - Player details data
 * @returns Promise<UpdatePlayerDetailsResponse>
 */
export const updatePlayerDetails = async (
  playerId: string, 
  playerData: UpdatePlayerDetailsRequest
): Promise<UpdatePlayerDetailsResponse> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    const response = await secureApi.post<any>(
      `/api/v1/club/team/update-player-details/${playerId}`,
      playerData
    );
    
    // Handle API response structure: may be wrapped in { statusCode, data: {...} } or direct
    const responseData = (response as any)?.data || response;

    // Return structured response matching the working pattern
    return {
      success: true,
      message: 'Player updated successfully',
      data: responseData
    };
  } catch (error: any) {
    console.error('Failed to update player details:', error);
    throw new Error(error.response?.data?.message || 'Failed to update player details');
  }
};

/**
 * Get individual player by ID for manage players context
 * @param playerId - The player ID
 * @returns Promise<ManagePlayer>
 */
export const getManagePlayerById = async (playerId: string): Promise<ManagePlayer> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    // APIClient handles token management automatically
    const response = await api.get(`${API_BASE_URL}/api/v1/club/team/player/find-by-id/${playerId}`, null);
    
    // Handle the API response structure
    if (response && response.data && response.data.data) {
      const playerData = response.data.data;
      
      // Convert API response to our ManagePlayer interface
      const player: ManagePlayer = {
        _id: playerData._id,
        playerId: playerData.playerId,
        name: playerData.name,
        profilePic: playerData.profilePic,
        dob: playerData.dob,
        ageCategory: playerData.ageCategory,
        parentName: playerData.parentName || playerData.parentname, // Support both camelCase and snake_case
        verificationStatus: playerData.verificationStatus,
        createdAt: playerData.createdAt,
        amountPaid: playerData.amountPaid,
        amountDue: playerData.amountDue,
        qrCode: playerData.qrCode,
        playerIdCard: playerData.playerIdCard,
        remarks: playerData.remarks || [],
        // Get the last remark from the remarks array
        remark: playerData.remarks && playerData.remarks.length > 0 
          ? playerData.remarks[playerData.remarks.length - 1].remark 
          : undefined,
        teamInfo: playerData.teamInfo ? {
          _id: playerData.teamInfo._id,
          club: playerData.teamInfo.club || '',
          name: playerData.teamInfo.name,
          logo: playerData.teamInfo.logo,
          clubInfo: playerData.teamInfo.clubInfo ? {
            _id: playerData.teamInfo.clubInfo._id,
            name: playerData.teamInfo.clubInfo.name,
            logo: playerData.teamInfo.clubInfo.logo
          } : undefined
        } : undefined,
        documents: playerData.documents ? playerData.documents.map((doc: any) => ({
          fileName: doc.fileName,
          key: doc.key,
          type: doc.type
        })) : []
      };
      
      return player;
    }
    
    // Fallback: try direct response.data structure
    if (response && response.data && response.data._id) {
      const playerData = response.data;
      return {
        _id: playerData._id,
        playerId: playerData.playerId,
        name: playerData.name,
        profilePic: playerData.profilePic,
        dob: playerData.dob,
        ageCategory: playerData.ageCategory,
        parentName: playerData.parentName || playerData.parentname, // Support both camelCase and snake_case
        verificationStatus: playerData.verificationStatus,
        createdAt: playerData.createdAt,
        amountPaid: playerData.amountPaid,
        amountDue: playerData.amountDue,
        qrCode: playerData.qrCode,
        playerIdCard: playerData.playerIdCard,
        remarks: playerData.remarks || [],
        // Get the last remark from the remarks array
        remark: playerData.remarks && playerData.remarks.length > 0 
          ? playerData.remarks[playerData.remarks.length - 1].remark 
          : undefined,
        teamInfo: playerData.teamInfo ? {
          _id: playerData.teamInfo._id,
          club: playerData.teamInfo.club || '',
          name: playerData.teamInfo.name,
          logo: playerData.teamInfo.logo,
          clubInfo: playerData.teamInfo.clubInfo ? {
            _id: playerData.teamInfo.clubInfo._id,
            name: playerData.teamInfo.clubInfo.name,
            logo: playerData.teamInfo.clubInfo.logo
          } : undefined
        } : undefined,
        documents: playerData.documents ? playerData.documents.map((doc: any) => ({
          fileName: doc.fileName,
          key: doc.key,
          type: doc.type
        })) : []
      };
    }
    
    throw new Error('Invalid response structure from player API');
  } catch (error) {
    console.error('Failed to fetch player by ID:', error);
    throw error;
  }
}; 